import React from 'react';
import PropTypes from 'prop-types';
import { cloneObject, manageEventListener } from '../../common/utils';

const defaultMaxHeight = '10em';

class DropdownArea extends React.Component {
  constructor(props) {
    super(props);
    const { isOpen: shouldOpen = false } = props;

    this.state = {
      isOpen: shouldOpen,
      isToggling: false,
      measuredMaxHeight: null
    };

    this.toggleDropdown = this.toggleDropdown.bind(this);
    this.manageTransitionEvent = this.manageTransitionEvent.bind(this);
    this.handleTransitionEvent = this.handleTransitionEvent.bind(this);
    this.measureMaxHeight = this.measureMaxHeight.bind(this);
    this.refDropdownArea = React.createRef();
  }

  componentDidMount() {
    const {
      measureMaxHeight,
      manageTransitionEvent
    } = this;

    measureMaxHeight();
    manageTransitionEvent('add');
  }

  componentDidUpdate() {
    const {
      props: {
        isOpen: shouldOpen,
        maxHeight: passedMaxHeight
      },
      state: {
        isOpen
      },
      toggleDropdown,
      measureMaxHeight
    } = this;

    if (!passedMaxHeight) {
      measureMaxHeight();
    }

    if (isOpen !== shouldOpen) {
      toggleDropdown();
    }
  }

  componentWillUnmount() {
    this.manageTransitionEvent('remove');
  }

  manageTransitionEvent(action) {
    const {
      handleTransitionEvent,
      refDropdownArea: {
        current: slideDownElement
      }
    } = this;

    manageEventListener(slideDownElement, 'transitionend', action, handleTransitionEvent);
  }

  toggleDropdown(isToggling = true) {
    const {
      state: {
        isToggling: wasToggling
      }
    } = this;

    if (isToggling !== wasToggling) {
      this.setState({ isToggling });
    }
  }

  measureMaxHeight() {
    const {
      state: {
        measuredMaxHeight: previousMeasurement
      },
      refDropdownArea: {
        current: dropdownAreaElement
      }
    } = this;

    const newMeasurement = dropdownAreaElement ? dropdownAreaElement.scrollHeight : defaultMaxHeight;
    if (newMeasurement !== previousMeasurement) {
      this.setState({ measuredMaxHeight: newMeasurement });
    }
  }

  handleTransitionEvent({ propertyName }) {
    if (propertyName === 'max-height') {
      this.setState(({ isOpen: wasOpen }) => ({
        isToggling: false,
        isOpen: !wasOpen
      }));
    }
  }

  render() {
    const {
      props: {
        maxHeight: passedMaxHeight,
        classNames: receivedClassNames = [],
        attributes: receivedAttributes = {},
        children
      },
      state: {
        isOpen,
        isToggling,
        measuredMaxHeight
      },
      refDropdownArea
    } = this;

    const openStateConditions = {
      open: !isToggling && isOpen,
      closed: !isToggling && !isOpen,
      opening: isToggling && !isOpen,
      closing: isToggling && isOpen
    };

    const maxHeight = passedMaxHeight || measuredMaxHeight;
    const classNames = [...receivedClassNames];
    const { className } = receivedAttributes;
    const { 0: openState } = Object.entries(openStateConditions).find(({ 1: conditionResult }) => !!conditionResult);

    classNames.push('slide-down-area', `state-${openState}`);

    if (className) {
      classNames.push(className);
    }

    const attributes = cloneObject(receivedAttributes, { className: classNames.join(' ') });

    if ((openState === 'open' || openState === 'opening') && maxHeight) {
      attributes.style = { maxHeight };
    }

    return <div {...attributes} ref={refDropdownArea}>{children}</div>;
  }
}

DropdownArea.propTypes = {
  maxHeight: PropTypes.string,
  isOpen: PropTypes.bool,
  classNames: PropTypes.arrayOf(PropTypes.string),
  attributes: PropTypes.object
};

export default DropdownArea;